import React from "react";
import { Form } from "react-bootstrap";
import {
  COUPON_ENFORCEMENT_MODE_HINTS,
  COUPON_ENFORCEMENT_MODES,
  CouponEnforcementMode,
  IPartnerConfigurationValues,
  OFFER_DISPLAY_MODE_HINTS,
  OFFER_DISPLAY_MODES,
  OfferDisplayMode,
} from "../../interfaces";

type FieldKey = keyof IPartnerConfigurationValues;

const NULLABLE = new Set<FieldKey>(["codelessDealsAllowedPercentage", "maxCoupons", "maxAutoCoupons", "maxOffers"]);
const LIMITS: Partial<Record<FieldKey, [number, number]>> = {
  codelessDealsAllowedPercentage: [0, 100],
  currentStanddownRulesVersion: [-1, 1_000_000],
  currentWhitelistDomainsVersion: [-1, 1_000_000],
};

export const WHITELIST_WARNING =
  "The whitelist version is normally generated automatically when a new whitelisted-domains list is published. " +
  "Setting it by hand points clients at a list that may not exist for every country. Change it anyway?";

let whitelistAcknowledged = false;

/** Asks once per page load before the whitelist version can be edited. */
export const confirmWhitelistEdit = (): boolean => {
  if (!whitelistAcknowledged) whitelistAcknowledged = window.confirm(WHITELIST_WARNING);
  return whitelistAcknowledged;
};

/** One editable value; numbers clamp to what the API accepts, blank nullable numbers mean "none". */
export const FieldInput: React.FC<{
  field: FieldKey;
  values: IPartnerConfigurationValues;
  onChange: (next: IPartnerConfigurationValues) => void;
  disabled?: boolean;
  compact?: boolean;
}> = ({ field, values, onChange, disabled, compact }) => {
  const set = (v: unknown) => onChange({ ...values, [field]: v });
  const value = values[field];

  if (field === "allowCodelessDeals") {
    return (
      <Form.Check type="switch" checked={!!value} disabled={disabled} onChange={(e) => set(e.target.checked)} label={compact ? undefined : value ? "On" : "Off"} />
    );
  }

  if (field === "couponEnforcementMode" || field === "offerDisplayMode") {
    const options = field === "couponEnforcementMode" ? COUPON_ENFORCEMENT_MODES : OFFER_DISPLAY_MODES;
    const hint = (o: string) =>
      field === "couponEnforcementMode" ? COUPON_ENFORCEMENT_MODE_HINTS[o as CouponEnforcementMode] : OFFER_DISPLAY_MODE_HINTS[o as OfferDisplayMode];
    const current = String(value ?? "");
    return (
      <Form.Select size="sm" value={current} disabled={disabled} onChange={(e) => set(e.target.value)} title={hint(current) ?? "The Platform API applies its default"} style={{ minWidth: 150 }}>
        {(current === "" || !(options as readonly string[]).includes(current)) && <option value={current}>{current || "Platform default"}</option>}
        {options.map((o) => (
          <option key={o} value={o} title={hint(o)}>
            {o}
          </option>
        ))}
      </Form.Select>
    );
  }

  const [min, max] = LIMITS[field] ?? [0, 1000];
  const guarded = field === "currentWhitelistDomainsVersion";
  return (
    <Form.Control
      size="sm"
      type="number"
      min={min}
      max={max}
      step={1}
      disabled={disabled}
      value={value === null || value === undefined ? "" : String(value)}
      placeholder={NULLABLE.has(field) ? (field === "codelessDealsAllowedPercentage" ? "Unthrottled" : "No cap") : undefined}
      style={{ width: compact ? 90 : 120 }}
      title={guarded ? "Normally auto-generated; you'll be asked to confirm before changing it" : undefined}
      onFocus={(e) => {
        if (guarded && !confirmWhitelistEdit()) e.currentTarget.blur();
      }}
      onChange={(e) => {
        if (guarded && !confirmWhitelistEdit()) return;
        const raw = e.target.value;
        if (raw === "") return set(NULLABLE.has(field) ? null : min);
        const n = Math.trunc(Number(raw));
        if (Number.isFinite(n)) set(Math.min(max, Math.max(min, n)));
      }}
    />
  );
};
